export type VoucherCard = { kind: 'token'; token: string } | { kind: 'code'; code: string }
export type LoginCard = { kind: 'login'; login: string; password: string }
export type CardCredential = VoucherCard | LoginCard

export const ENTRY_METHODS = ['CODE', 'QR_SCAN', 'QR_IMAGE', 'QR_LINK'] as const
export type EntryMethod = (typeof ENTRY_METHODS)[number]

const CODE_SHAPE = /^[A-Z0-9-]{6,44}$/
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{40,64}$/
const TOKEN_PREFIX = 't.'
const CARD_LINK_PATH = /\/v\/?$/
const CARD_LOGIN_SHAPE = /^\d{6,12}$/
const CARD_PASSWORD_SHAPE = /^\d{4,12}$/

export const CARD_DIGITS_MAX = 12

export function cardDigits(raw: string): string {
  return raw.replace(/\D+/g, '').slice(0, CARD_DIGITS_MAX)
}

export function loginCard(login: string, password: string): LoginCard | null {
  return CARD_LOGIN_SHAPE.test(login) && CARD_PASSWORD_SHAPE.test(password)
    ? { kind: 'login', login, password }
    : null
}

const LOGIN_CARD_REJECTION_KEY: Record<string, string> = {
  VOUCHER_NOT_FOUND: 'error.cardLoginWrong',
  VOUCHER_ACTIVATED: 'error.cardPackageEnded',
  VOUCHER_EXPIRED: 'error.cardPackageEnded',
  VOUCHER_LOCKED: 'error.cardLocked',
}

export function loginCardRejectionKey(status: number | null, tag: string | null): string | null {
  return status === 409 && tag !== null ? (LOGIN_CARD_REJECTION_KEY[tag] ?? null) : null
}

export function normalizeVoucherCode(raw: string): string | null {
  const code = raw.replace(/\s+/g, '').toUpperCase()
  return CODE_SHAPE.test(code) ? code : null
}

export function normalizeQrToken(raw: string): string | null {
  const token = raw.trim()
  return TOKEN_SHAPE.test(token) ? token : null
}

export function cardFromFragment(hash: string): VoucherCard | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return null
  }
  if (decoded.startsWith(TOKEN_PREFIX)) {
    const token = normalizeQrToken(decoded.slice(TOKEN_PREFIX.length))
    return token ? { kind: 'token', token } : null
  }
  const code = normalizeVoucherCode(decoded)
  return code ? { kind: 'code', code } : null
}

export function cardFromQrText(text: string): VoucherCard | null {
  const trimmed = text.trim()
  if (/^https?:\/\//i.test(trimmed)) {
    let url: URL
    try {
      url = new URL(trimmed)
    } catch {
      return null
    }
    return CARD_LINK_PATH.test(url.pathname) ? cardFromFragment(url.hash) : null
  }
  const code = normalizeVoucherCode(trimmed)
  return code ? { kind: 'code', code } : null
}

export function sameCard(a: CardCredential | null, b: CardCredential | null): boolean {
  if (a === null || b === null) return false
  if (a.kind === 'token' && b.kind === 'token') return a.token === b.token
  if (a.kind === 'code' && b.kind === 'code') return a.code === b.code
  return false
}

export function voucherCardFields(card: VoucherCard): { code: string } | { qrToken: string } {
  return card.kind === 'token' ? { qrToken: card.token } : { code: card.code }
}

export function cardCredentialFields(
  card: CardCredential,
): { code: string } | { qrToken: string } | { code: string; password: string } {
  return card.kind === 'login' ? { code: card.login, password: card.password } : voucherCardFields(card)
}
